import React, { useRef, useState } from 'react';
import { Cloud, Upload, X, FileText, AlertCircle } from 'lucide-react';

const DocumentUploadSection = ({ 
  documentType, 
  uploadedFile, 
  onFileUpload, 
  onFileRemove 
}) => {
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const validateFile = (file) => {
    const maxSize = 10 * 1024 * 1024; // 10MB
    const acceptedTypes = documentType?.acceptedTypes?.split(',') || [];
    const fileExtension = '.' + file?.name?.split('.')?.pop()?.toLowerCase();

    if (file?.size > maxSize) {
      return 'File size must be less than 10MB';
    }

    if (acceptedTypes?.length > 0 && !acceptedTypes?.includes(fileExtension)) {
      return `Please upload a file with one of these formats: ${acceptedTypes?.join(', ')}`;
    }

    return null;
  };

  const handleFileSelect = (file) => {
    const error = validateFile(file);
    if (error) {
      setUploadError(error);
      return;
    }

    setUploadError('');
    onFileUpload?.(file);
  };

  const handleClick = () => {
    fileInputRef?.current?.click();
  };

  const handleDrag = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e?.type === 'dragenter' || e?.type === 'dragover') {
      setDragActive(true);
    } else if (e?.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(false);

    const files = e?.dataTransfer?.files;
    if (files?.[0]) {
      handleFileSelect(files?.[0]);
    }
  };

  const handleInputChange = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  return (
    <div className="space-y-2">
      {/* Upload Area */}
      <div
        className={`
          border-2 border-dashed rounded-lg p-8 transition-all cursor-pointer
          ${dragActive 
            ? 'border-blue-500 bg-blue-50' 
            : uploadedFile 
              ? 'border-green-400 bg-green-50' :'border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100'
          }
        `}
        onClick={handleClick}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept={documentType?.acceptedTypes}
          onChange={handleInputChange}
        />

        <div className="text-center">
          {uploadedFile ? (
            // File uploaded state
            (<div className="space-y-3">
              <div className="flex items-center justify-center">
                <FileText className="w-12 h-12 text-green-500" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {uploadedFile?.name}
                </p>
                <p className="text-xs text-gray-500">
                  {formatFileSize(uploadedFile?.size)}
                </p>
              </div>
              <button
                onClick={(e) => {
                  e?.stopPropagation();
                  onFileRemove?.();
                }}
                className="inline-flex items-center text-sm text-red-600 hover:text-red-700"
              >
                <X className="w-4 h-4 mr-1" />
                Remove file
              </button>
            </div>)
          ) : (
            // Empty upload state
            (<div className="space-y-3">
              <div className="flex items-center justify-center">
                <Cloud className="w-12 h-12 text-gray-400" />
              </div>
              <div>
                <p className="text-lg font-semibold text-gray-900">
                  {documentType?.label}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  {documentType?.description}
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  Click to upload or drag and drop
                </p>
                <p className="text-xs text-gray-400">
                  Supported formats: {documentType?.acceptedTypes} (Max 10MB)
                </p>
              </div>
            </div>)
          )}
        </div>
      </div>
      {/* Error Message */}
      {uploadError && (
        <div className="flex items-center space-x-2 text-red-600 text-sm">
          <AlertCircle className="w-4 h-4" />
          <span>{uploadError}</span>
        </div>
      )}
    </div>
  );
};

export default DocumentUploadSection;